import React, { useState, useEffect, useMemo } from "react"; // Add useEffect
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ComboBox from "../../components/ComboBox";
import {
  useGetprovidersListForProviderDetails,
  useGetPurchaseVendorBySponsor,
  useListOfProviderLocations,
  useListOfQuestionnaireTemplate,
  useSendRfiToPurchaseVendor,
  usePostRequestAccess,
  useAddProviderContact,
  useAddProviderLocation,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import { existingRFIRequestValidationSchema } from "../../Schema/RequestRfiSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import Select, { components } from "react-select";
import { animatedComponents, newVendorRightsList } from "../../utils/constants";
import TemplateGroupedDropdown from "../../components/TemplateGroupedDropdown";
import { useDispatch, useSelector } from "react-redux";
import { clearSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import DatePickerController from "../../components/datePickerController";
import { useNavigate } from "react-router-dom";
import NotificationModal from "../../components/NotificationModal";
import { dateFormatRFi } from "../../utils/helperMethods";
import { dedupeVqqTemplates } from "../../utils/dedupeVqqTemplates";
import { buildVqqLocationPart } from "../../utils/vqqNameFormat";
import LoadingModal from "../../components/Skeletons/LoadingModal";
import { useUser } from "@clerk/clerk-react";
import ProviderContactDrawer from "../../components/ProviderContactDrawer";
import {
  collectVqqContactsToProvision,
  ensureLegacyRequestContacts,
} from "../../utils/requestContactProvisioning";
import { useDecodedToken } from "../../custom-hooks/useToken";
import {
  getSponsorOrgIdFromToken,
  resolveRequestVqqPurchaseVendor,
} from "../../utils/ensurePurchaseVendorForSponsor";
import { resolveVendorHyperComplyOrganization } from "../../utils/vendorHyperComplyOrganization";

import { CheckIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon as ChevronDownIconSolid } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  useCanRequestAutoScore,
  useCanRequestSmeScore,
} from "../../utils/orgFeatureGates";
import { resolveRiskAddOnRequest } from "../../utils/vqqRiskAddOns";
import {
  countryOptions,
  getCountryByValue,
  getFilteredCountryOptions,
  getFilteredStateOptions,
  getStateOptions,
  parseAddressComponents,
} from "../../utils/locationData";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "../../config/googleMapsConfig";
import { onBlurSelectWithDelay } from "../../utils/dom-events";
import LocationFields from "../../components/LocationFields";
import ContactFields from "../../components/ContactFields";
import { getLocationFromProvider } from "../../utils/addressUtils";
import CustomTooltip from "../../components/Filters/CustomTooltip";
import { isDuringMaintenance } from "../../utils/maintenance";
import VendorListedServicesSuggestions, {
  useVendorProfileServiceCategories,
} from "../../components/VendorListedServicesSuggestions";
import RequestContactMultiSelect from "../../components/RequestContactMultiSelect";
import RequestContactInlineForm from "../../components/RequestContactInlineForm";
import {
  buildRequestContactsPayload,
  buildVqqContactEmails,
  resolveContactPersonName,
  resolveRequestPrimaryContact,
  validateRequestContactSelection,
} from "../../utils/requestContactFormUtils";
import {
  CONTACT_CATEGORY,
  getDefaultApiContactsSelection,
  getRequestFormContacts,
  mapApiContactToMultiSelectOption,
} from "../../utils/requestContactDesignations";
import useRequestFormServicePreselection from "../../custom-hooks/useRequestFormServicePreselection";
import {
  buildVqqTemplateOptions,
  getSuggestedVqqTemplateValueSet,
} from "../../utils/requestFormServicePreselection";

const newContactObj = {
  id: "new-contact",
  firstName: "",
  lastName: "",
  email: "",
  fullContact: "",
  primary: false,
};

export const RequestRFis = () => {
  // State for contact drawer
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [selectedRequestContacts, setSelectedRequestContacts] = useState([]);
  const [showAddContactForm, setShowAddContactForm] = useState(false);

  // 14028 — Risk Scoring add-on selection. Each checkbox is visible only when
  // the sponsor org is entitled AND has remaining quota (Unlimited counts as
  // remaining). If both are hidden, the entire Add-Ons group is hidden too.
  const canRequestAutoScore = useCanRequestAutoScore();
  const canRequestSmeScore = useCanRequestSmeScore();
  const [enableAutoScore, setEnableAutoScore] = useState(false);
  const [enableSmeScore, setEnableSmeScore] = useState(false);

  // Handler to add new contact from drawer
  const handleAddContact = (contact) => {
    setAdditionalContacts((prev) => [...prev, contact]);
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // const { data: providers } = useGetprovidersListForProviderDetails();
  const { data: providers } = useGetprovidersListForProviderDetails();
  const { data: questionnares, isLoading: questionnairesLoading } =
    useListOfQuestionnaireTemplate();
  const { data: rfis } = UseGetRfiListForFilters();
  const { mutateAsync: sendRfis } = useSendRfiToPurchaseVendor();
  const { mutateAsync: requestLibraryAccess } = usePostRequestAccess();
  const [isLoading, setLoading] = useState(false);
  const methods = useForm({
    resolver: yupResolver(existingRFIRequestValidationSchema),
    mode: "onTouched",
    defaultValues: { rfiLocationId: "" },
  });
  const { mutateAsync: addProviderContact } = useAddProviderContact();
  const { mutateAsync: addProviderLocation } = useAddProviderLocation();

  const [isRFIContactDropdownOpen, setIsRFIContactDropdownOpen] =
    useState(false);
  const [currentSelectedRFIContact, currentSetSelectedRFIContact] =
    useState(null);

  const [primaryAddress, setPrimaryAddress] = useState("");
  const [primaryCity, setPrimaryCity] = useState("");
  const [primaryCountry, setPrimaryCountry] = useState("");
  const [primaryState, setPrimaryState] = useState("");
  const [primaryCountrySearch, setPrimaryCountrySearch] = useState("");
  const [primaryStateSearch, setPrimaryStateSearch] = useState("");
  const [isRFILocationDropdownOpen, setIsRFILocationDropdownOpen] =
    useState(false);
  const [currentSelectedRFILocation, currentSetSelectedRFILocation] =
    useState(null);

  const [autocomplete, setAutocomplete] = useState(null);
  const [isPrimaryCountryDropdownOpen, setIsPrimaryCountryDropdownOpen] =
    useState(false);
  const [isPrimaryStateDropdownOpen, setIsPrimaryStateDropdownOpen] =
    useState(false);

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    control,
    register,
    formState: { errors },
    reset,
    resetField,
  } = methods;
  // Sync selected RFI location id to form for validation (primary location required only when "new-location")
  useEffect(() => {
    setValue("rfiLocationId", currentSelectedRFILocation?.id ?? "");
  }, [currentSelectedRFILocation, setValue]);
  const { data: providerLocations, refetch: refetchLocations } =
    useListOfProviderLocations(watch("selectedProvider")?.provider_id);
  const { data: purchaseVendorBySponsor } = useGetPurchaseVendorBySponsor(
    watch("selectedProvider")?.provider_id,
  );

  const { user } = useUser();
  const { decoded } = useDecodedToken();
  const { hcUserId } = useSelector((state) => state.userRights);

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#AB54DB" : "white", // Purple background on hover
      color: state.isFocused ? "white" : "black", // White text on hover for better contrast
    }),
  };

  const optionsForMultiSelectLocations = useMemo(() => {
    // Debug the structure

    // Check if data exists and is an array
    if (!providerLocations || !Array.isArray(providerLocations)) {
      return [];
    }

    // Create options array
    const options = [];

    // Process each provider in the array
    providerLocations.forEach((provider) => {
      // Check if provider has provider_locations array
      if (
        provider.provider_locations &&
        Array.isArray(provider.provider_locations)
      ) {
        // Sort locations by location_type: primary, secondary, additional
        const sortedLocations = provider.provider_locations.sort((a, b) => {
          const typeOrder = { primary: 1, secondary: 2, additional: 3 };
          const aOrder = typeOrder[a.location_type] || 4;
          const bOrder = typeOrder[b.location_type] || 4;
          return aOrder - bOrder;
        });

        // Process each location in the sorted provider_locations array
        sortedLocations.forEach((location) => {
          // Add address1 as an option if it exists
          if (location.address1) {
            // For primary locations, append provider's city and country to address1
            let addressLabel = location.address1;
            if (location.location_type === "primary") {
              const cityCountry = [];
              if (provider.city) cityCountry.push(provider.city);
              if (provider.country) cityCountry.push(provider.country);

              if (cityCountry.length > 0) {
                addressLabel = `${location.address1}, ${cityCountry.join(
                  ", ",
                )}`;
              }
            }

            options.push({
              value: `${location.provider_location_id}_address1`,
              label: addressLabel,
              locationId: location.provider_location_id,
              addressType: "address1",
              locationType: location.location_type,
            });
          }

          // Add address2 as a separate option if it exists
          if (location.address2) {
            options.push({
              value: `${location.provider_location_id}_address2`,
              label: location.address2,
              locationId: location.provider_location_id,
              addressType: "address2",
              locationType: location.location_type,
            });
          }
        });
      }
    });

    return options;
  }, [providerLocations]);

  const optionsForMultiSelect = useMemo(
    () => buildVqqTemplateOptions(questionnares),
    [questionnares],
  );

  const [searchParams] = useSearchParams();
  const providerId = searchParams.get("id");
  const templateId = searchParams.get("templateId");
  const templateName = searchParams.get("templateName");
  const libraryDocumentId = searchParams.get("documentId");

  useEffect(() => {
    if (!providerId || !providers?.length) return;
    const selectedProvider = providers.find(
      (p) => String(p.provider_id) === String(providerId),
    );
    if (selectedProvider) {
      setValue("selectedProvider", selectedProvider);
      setValue("selectedContact", null);
    }
  }, [providerId, providers, setValue]);

  // Watch the selected provider
  const selectedProvider = watch("selectedProvider");
  const selectedServiceCategories = watch("serviceCategories") || [];
  // Risk scoring add-ons exist only for Diligent templates: a custom template
  // hides the whole block and sends both flags off.
  const riskAddOns = resolveRiskAddOnRequest({ templates: selectedServiceCategories, canRequestAutoScore, canRequestSmeScore, enableAutoScore, enableSmeScore });
  const { resolvedProvider, isLoadingCapabilities } =
    useVendorProfileServiceCategories(
      selectedProvider,
      selectedProvider?.provider_id,
    );

  const suggestedTemplateValues = useMemo(
    () =>
      getSuggestedVqqTemplateValueSet(
        resolvedProvider,
        optionsForMultiSelect || [],
      ),
    [resolvedProvider, optionsForMultiSelect],
  );

  const { setServicesWithUserTracking } = useRequestFormServicePreselection({
    mode: "vqq",
    provider: selectedProvider,
    providerId: selectedProvider?.provider_id,
    urlTemplateId: templateId,
    urlTemplateName: templateName,
    questionnairesLoading,
    questionnaires: questionnares,
    templateOptions: optionsForMultiSelect,
    isLoadingCapabilities,
    resolvedProvider,
    selectedServices: selectedServiceCategories,
    setServiceCategories: (next) => setValue("serviceCategories", next),
  });
  const getContactsForProvider = (provider) =>
    getRequestFormContacts(provider, CONTACT_CATEGORY.RFI);

  const existingLocations = {};
  const availableLocations = useMemo(
    () => [
      ...(providerLocations
        ?.map((loc) => loc.provider_locations)
        ?.flat()
        ?.filter((loc) => loc.address || loc.address1)
        ?.sort((a, b) => a.location_type === "primary" ? -1 : a.created_at > b.created_at ? 1 : -1)
        ?.map((loc, i) => ({
          ...loc,
          id: Date.now() + i,
          address: loc.address || loc.address1,
          label:
            loc.location_type === "primary" || i === 0
              ? "Primary Location"
              : `Additional Location ${i}`,
          fullAddress: getLocationFromProvider(loc, { short: false, abbvState: true }),
        }))
        .filter((loc) => {
          const exist = !existingLocations[loc.fullAddress];
          existingLocations[loc.fullAddress] = true;
          return exist;
        }) || []),
      {
        id: "new-location",
        label: "",
        fullAddress: "",
      },
    ],
    [providerLocations],
  );

  const getApiContacts = () => getContactsForProvider(watch("selectedProvider"));

  const apiContacts = getApiContacts();
  const allContacts = [...apiContacts, ...additionalContacts];
  const hasDisplayableApiContacts = apiContacts.length > 0;
  useEffect(() => {
    if (selectedProvider && hasDisplayableApiContacts) {
      setSelectedRequestContacts(getDefaultApiContactsSelection(apiContacts));
      setShowAddContactForm(false);
      setAdditionalContacts([]);
    } else if (selectedProvider) {
      setSelectedRequestContacts([]);
      setShowAddContactForm(true);
    }
  }, [selectedProvider?.provider_id, hasDisplayableApiContacts, apiContacts.length]);

  const availableContacts = [
    ...(allContacts
      .map((contact, index) => ({
        id: contact.email || `contact-${index}`, // Add unique id for each contact
        ...contact, // Keep all original properties
        firstName: contact.first_name || "",
        lastName: contact.last_name || "",
        name: contact.name || "",
        email: contact.email || "",
        fullContact: [
          [contact.first_name, contact.last_name, contact.title].join(" "),
          // contact.email,
        ].join(", "),
        label:
          index === 0 && contact.email === apiContacts[0]?.email
            ? "Preferred Contact"
            : `Additional Contact ${index}`,
        // Set primary flag for the first provider_user contact
        primary: index === 0 && contact.email === apiContacts[0]?.email,
      }))
      .filter((loc) => loc.firstName) || []),
    newContactObj,
  ];

  useEffect(() => {
    if (selectedProvider && availableLocations.length > 0) {
      currentSetSelectedRFILocation(availableLocations?.find(loc => loc.location_type === "primary") || availableLocations[0]);
    }
  }, [selectedProvider, availableLocations]);

  useEffect(() => {
    if (
      availableContacts.length > 0 &&
      !currentSelectedRFIContact &&
      availableContacts[0].id !== "new-contact"
    ) {
      currentSetSelectedRFIContact(availableContacts[0]);
    }
    if (
      currentSelectedRFIContact &&
      !availableContacts.find((loc) => loc.id === currentSelectedRFIContact.id)
    ) {
      currentSetSelectedRFIContact(
        availableContacts.length > 0 ? availableContacts[0] : null,
      );
    }
  }, [
    availableContacts,
    currentSelectedRFIContact,
    currentSetSelectedRFIContact,
  ]);

  const onSubmit = async (data) => {
    setLoading(true);

    if (!data?.selectedProvider) {
      setLoading(false);
      return;
    }

    const contactError = validateRequestContactSelection(
      selectedRequestContacts,
      hasDisplayableApiContacts,
    );
    if (contactError) {
      setError("rfiContact", { type: "manual", message: contactError });
      setLoading(false);
      return;
    }
    clearErrors("rfiContact");

    // Legacy additional-contact fields when vendor has no API contacts at all
    if (!hasDisplayableApiContacts && additionalContacts?.length > 0 && selectedRequestContacts.length === 0) {
      const first = additionalContacts[0];
      const prefix = "additional_0_";
      clearErrors([
        `${prefix}FirstName`,
        `${prefix}LastName`,
        `${prefix}Title`,
        `${prefix}Email`,
      ]);
      let hasError = false;
      if (!String(first?.firstName ?? "").trim()) {
        setError(`${prefix}FirstName`, { type: "manual", message: "First name is required" });
        hasError = true;
      }
      if (!String(first?.lastName ?? "").trim()) {
        setError(`${prefix}LastName`, { type: "manual", message: "Last name is required" });
        hasError = true;
      }
      if (!String(first?.title ?? "").trim()) {
        setError(`${prefix}Title`, { type: "manual", message: "Job title is required" });
        hasError = true;
      }
      if (!String(first?.email ?? "").trim()) {
        setError(`${prefix}Email`, { type: "manual", message: "Email is required" });
        hasError = true;
      }
      if (hasError) {
        setLoading(false);
        return;
      }
    }

    const providerDataRoles = {
      provider_user_subrole: [
        {
          Subrole: "Vendor Admin",
        },
      ],
      provider_user_permissions: newVendorRightsList.map((permission) => ({
        permission: permission,
      })),
    };

    const currentRFILocation = currentSelectedRFILocation?.id === "new-location"
    ? {
      address: primaryAddress,
      city: primaryCity,
      state: primaryState,
      country: primaryCountry
    } : currentSelectedRFILocation;
    

    const providerId = data.selectedProvider?.provider_id || "";
    const providerName = data.selectedProvider?.name || "";
    const sponsorId = user?.publicMetadata?.sponsor_id;
    const sponsorOrgId = getSponsorOrgIdFromToken(decoded);

    // Resolve the primary contact to send to Hypercomply when the purchase vendor has to be
    // created: an existing selected contact, otherwise the contact the user typed into the
    // Additional Contact form. Hypercomply requires a non-null contact_email on create, so it
    // must travel on the create call itself (the provider contact is provisioned separately below).
    const primaryContact =
      selectedRequestContacts.length > 0
        ? resolveRequestPrimaryContact(selectedRequestContacts)
        : currentSelectedRFIContact &&
            currentSelectedRFIContact.id !== "new-contact"
          ? currentSelectedRFIContact
          : additionalContacts[0] || {};

    const primaryContactEmail = (
      primaryContact?.email || data?.contactEmail || ""
    ).trim();

    const primaryContactName = resolveContactPersonName(primaryContact, {
      firstName: data?.contactFirstName,
      lastName: data?.contactLastName,
    });

    // Create the sponsor's purchase vendor link in Hypercomply when it does not exist yet
    // (#14237) instead of failing the submit.
    const purchaseVendorResult = await resolveRequestVqqPurchaseVendor({
      existingPurchaseVendorId: purchaseVendorBySponsor?.hc_purchase_vendor_id,
      providerId,
      sponsorId,
      orgId: sponsorOrgId,
      contactEmail: primaryContactEmail,
      contactName: primaryContactName,
    });

    if (!purchaseVendorResult.success) {
      setIsError(true);
      setNotificationMessage(purchaseVendorResult.message);
      setShowNotification(true);
      setLoading(false);
      return;
    }

    const purchaseVendorId = purchaseVendorResult.purchaseVendorId;

    const vendorOrganization = await resolveVendorHyperComplyOrganization({
      providerId,
      organizationId: data.selectedProvider?.org_id,
    });
    if (!vendorOrganization.success) {
      setIsError(true);
      setNotificationMessage(vendorOrganization.message);
      setShowNotification(true);
      setLoading(false);
      return;
    }
    const organization_id = vendorOrganization.organizationId;

    const knownVendorEmails = new Set(
      apiContacts
        .map((c) => c.email?.trim()?.toLowerCase())
        .filter(Boolean),
    );

    const contactsToEnsure = collectVqqContactsToProvision({
      selectedRequestContacts,
      allContacts,
      currentSelectedContact: currentSelectedRFIContact,
      additionalContacts,
      formContact: {
        email: data?.contactEmail || "",
        first_name: data?.contactFirstName || "",
        last_name: data?.contactLastName || "",
        job_title: additionalContacts?.[0]?.title || "",
      },
      knownVendorEmails,
    });

    try {
      await ensureLegacyRequestContacts({
        contacts: contactsToEnsure,
        providerId,
        organization_id,
        providerName,
        providerDataRoles,
        addProviderContact,
      });
    } catch (error) {
      setIsError(true);
      setNotificationMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add contact. Please check the contact details and try again.",
      );
      setShowNotification(true);
      setLoading(false);
      return;
    }

    if (currentSelectedRFILocation?.id === "new-location") {
      try {
        await addProviderLocation({
          provider_id: providerId,
          location: {
            address1: primaryAddress || "",
            city: primaryCity || "",
            country: primaryCountry || "",
            state: primaryState || "",
            provider_id: providerId,
            location_type: "additional",
          },
        });
        refetchLocations();
      } catch (error) {
        setIsError(true);
        setNotificationMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to add location. Please check the address and try again.",
        );
        setShowNotification(true);
        setLoading(false);
        return;
      }
    }

    const rfiContactEmail =
      currentSelectedRFIContact?.id === "new-contact"
        ? additionalContacts[0]?.email
        : currentSelectedRFIContact?.email;

    const rfiContactEmails = buildVqqContactEmails(selectedRequestContacts);
    const requestContactsPayload =
      buildRequestContactsPayload(selectedRequestContacts);

    try {
      let libraryQuestionnaireId = null;
      for (const services of dedupeVqqTemplates(data?.serviceCategories)) {
        // The locator has to be the same "City, State" the app shows on the VQQ, otherwise
        // HyperComply and D360 disagree on the name.
        const locationPart =
          buildVqqLocationPart(currentRFILocation?.city, currentRFILocation?.state) ||
          getLocationFromProvider(currentRFILocation, { short: true, abbvState: true });
        const uniqueName = `Diligent - ${services?.label} - ${dateFormatRFi(
          data.expectedDate?.startDate,
        ).toUpperCase()} - ${locationPart}`;

        const sendResult = await sendRfis({
          id: purchaseVendorId,
          data: {
            name: uniqueName,
            owner_id: hcUserId,
            template_id: services?.value,
            due_by: data?.expectedDate?.startDate,
            invite_message: data.additionalDetails || "",
            send_questionnaire: true,
            vendor_name: providerName,
            contact_name: primaryContactName,
            template_name: services?.label || "",
            emails:
              rfiContactEmails.length > 0
                ? rfiContactEmails
                : [rfiContactEmail?.toLowerCase() || ""],
            request_contacts: requestContactsPayload,
            provider_id: providerId,
            is_global_template: services?.global || false,
            location: currentRFILocation?.address || "",
            location_city: currentRFILocation?.city || "",
            location_state: currentRFILocation?.state || "",
            location_country: currentRFILocation?.country || "",
            ...riskAddOns.forTemplate(services),
          },
        });
        const questionnaireId = Number(
          sendResult?.id ?? sendResult?.questionnaire_id,
        );
        if (
          libraryDocumentId &&
          Number.isFinite(questionnaireId) &&
          questionnaireId > 0 &&
          libraryQuestionnaireId == null
        ) {
          libraryQuestionnaireId = questionnaireId;
        }
      }

      if (libraryDocumentId) {
        try {
          await requestLibraryAccess({
            documentId: libraryDocumentId,
            hcQuestionnaireId: libraryQuestionnaireId,
          });
        } catch {
          // Questionnaire already sent; do not fail the VQQ submit if the library row cannot be created.
        }
      }

      setIsError(false);
      setNotificationMessage("VQQ request submitted successfully");
      setShowNotification(true);
      setTimeout(() => {
        reset();
        dispatch(clearSearchTextByPage({ pageKey: "myRfis" }));
        navigate("/RFI/History");
      }, 2000);
    } catch (error) {
      setIsError(true);
      setNotificationMessage(
        error?.response?.data?.message || "Failed to submit VQQ request",
      );
      setShowNotification(true);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingModal open={isLoading} />;
  }

  // Check if form can be submitted
  const canSubmit = () => {
    if (selectedRequestContacts.length > 0) return true;

    const hasContacts = allContacts?.length > 0;
    if (hasContacts) return true;

    // Legacy additional-contact fields when the old inline form is shown
    const firstName = watch("contactFirstName");
    const lastName = watch("contactLastName");
    const email = watch("contactEmail");

    return (
      firstName &&
      lastName &&
      email &&
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      email.trim() !== ""
    );
  };

  const addAdditionalContact = () => {
    setAdditionalContacts((prev) => [
      ...prev,
      {
        firstName: "",
        lastName: "",
        title: "",
        email: "",
      },
    ]);
  };

  const updateAdditionalContact = (index, field, value) => {
    setAdditionalContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact,
      ),
    );
  };

  const removeAdditionalContact = (index) => {
    setAdditionalContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const cancelAddContactForm = () => {
    setShowAddContactForm(false);
    setAdditionalContacts([]);
    if (hasDisplayableApiContacts && apiContacts.length > 0) {
      const primary = apiContacts.find((c) => c.isCategoryPrimary) || apiContacts[0];
      const defaultSelected = getDefaultApiContactsSelection(apiContacts);
      setSelectedRequestContacts(defaultSelected);
      currentSetSelectedRFIContact(mapApiContactToMultiSelectOption(primary));
    } else {
      setSelectedRequestContacts([]);
      currentSetSelectedRFIContact(null);
    }
  };

  const handleOnLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      const { streetAddress, city, state, countryCode } =
        parseAddressComponents(place);

      setPrimaryAddress(streetAddress);
      setPrimaryCity(city);
      setPrimaryState(state);
      setPrimaryCountry(countryCode);

      setValue("primaryAddress", streetAddress);
      setValue("primaryCity", city);
      setValue("primaryState", state);
      setValue("primaryCountry", countryCode);
      setIsPrimaryCountryDropdownOpen(false);
      setIsPrimaryStateDropdownOpen(false);
    }
  };

  return (
    <div className="container">
      <PageHeader
        heading={"Vendor Qualification Questionnaire (VQQ)"}
        subHeading={
          "Complete the information below to request a Vendor Qualification Questionnaire (VQQ) document/(s) to be completed by a selected vendor."
        }
        marginLeft={"ml-5"}
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Vendor Name
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <ComboBox
                  data={providers}
                  placeHolder="Select Vendors"
                  setSelected={(value) => {
                    setValue("selectedProvider", value);
                    resetField("expectedDate", { defaultValue: null });
                    resetField("additionalDetails", { defaultValue: "" });
                  }}
                  selected={watch("selectedProvider")}
                  width={"w-[500px]"}
                  inputWidth={"w-[500px]"}
                  showContactFormatting={true}
                />
              </span>
              {errors.selectedProvider && (
                <p className="text-red-500 text-sm">
                  {errors.selectedProvider.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col border-b border-gray-300 py-5 lg:py-6">
            <div className="ml-5  sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 py-5 lg:py-6">
              <div className="flex items-center justify-between">
                <h1 className="block text-sm font-medium text-gray-900 md:mt-0">
                  Contact
                </h1>
              </div>
              <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                {hasDisplayableApiContacts ? (
                  <>
                    <RequestContactMultiSelect
                      label="Request Contact(s)"
                      placeholder="Select request contact(s)"
                      contacts={apiContacts}
                      selectedContacts={selectedRequestContacts}
                      onChange={setSelectedRequestContacts}
                      isAddingNewContact={showAddContactForm}
                      onAddNewContact={() => {
                        setShowAddContactForm(true);
                      }}
                    />
                    {showAddContactForm && (
                      <RequestContactInlineForm
                        onAdd={(contact) => {
                          setSelectedRequestContacts((prev) => [...prev, contact]);
                          setShowAddContactForm(false);
                        }}
                        onCancel={() => setShowAddContactForm(false)}
                      />
                    )}
                  </>
                ) : selectedProvider ? (
                  <>
                    {selectedRequestContacts.length > 0 && (
                      <RequestContactMultiSelect
                        label="Request Contact(s)"
                        placeholder="Select request contact(s)"
                        contacts={[]}
                        selectedContacts={selectedRequestContacts}
                        onChange={setSelectedRequestContacts}
                        addNewContactLabel="Add another contact"
                        isAddingNewContact={showAddContactForm}
                        onAddNewContact={() => setShowAddContactForm(true)}
                      />
                    )}
                    {(showAddContactForm || selectedRequestContacts.length === 0) && (
                      <RequestContactInlineForm
                        onAdd={(contact) => {
                          setSelectedRequestContacts((prev) => [...prev, contact]);
                          setShowAddContactForm(false);
                        }}
                        onCancel={() => setShowAddContactForm(false)}
                      />
                    )}
                  </>
                ) : (
                  <div className="sm:max-w-xl text-sm sm:pt-1.5">
                    <span className="text-gray-500 italic">
                      Please first select or add a vendor in the section above.
                    </span>
                  </div>
                )}
                {errors.rfiContact && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.rfiContact}
                  </p>
                )}
              </div>
            </div>

            {!(selectedProvider && !hasDisplayableApiContacts) &&
              (availableContacts?.length === 0 ||
              showAddContactForm ||
              currentSelectedRFIContact?.id === "new-contact") &&
              additionalContacts.map((contact, index) => {
                const contactLabel =
                  index === 0
                    ? "Additional Contact"
                    : `Additional Contact ${index + 1}`;
                const isRequiredContact =
                  apiContacts?.length === 0 && index === 0;
                return (
                  <div
                    key={index}
                    className={`ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 ${index === 0 ? "sm:border-t sm:border-gray-200" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        {contactLabel}
                        {isRequiredContact && (
                          <span className="text-red-600 ml-0.5" aria-hidden>
                            *
                          </span>
                        )}
                      </label>
                      {(showAddContactForm && hasDisplayableApiContacts) ||
                      availableContacts?.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (showAddContactForm && hasDisplayableApiContacts) {
                              cancelAddContactForm();
                            } else {
                              removeAdditionalContact(index);
                              currentSetSelectedRFIContact(null);
                            }
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          aria-label="Remove contact"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                    <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                      <ContactFields
                        errors={errors}
                        setValue={setValue}
                        fieldPrefix={`additional_${index}_`}
                        firstName={contact.firstName}
                        setFirstName={(v) =>
                          updateAdditionalContact(index, "firstName", v)
                        }
                        lastName={contact.lastName}
                        setLastName={(v) =>
                          updateAdditionalContact(index, "lastName", v)
                        }
                        title={contact.title}
                        setTitle={(v) =>
                          updateAdditionalContact(index, "title", v)
                        }
                        email={contact.email}
                        setEmail={(v) =>
                          updateAdditionalContact(index, "email", v)
                        }
                        fullWidth
                        errorHighlight={false}
                      />
                    </div>
                  </div>
                );
              })}

            {/* Add Additional Contact Button - Left margin */}
            {/* <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-6 sm:pb-2">
              <div className="sm:col-span-1">
                <button
                  type="button"
                  onClick={addAdditionalContact}
                  className="text-indigo-600 text-sm cursor-pointer hover:underline hover:text-indigo-700 focus:outline-none"
                >
                  Add Additional Contact
                </button>
              </div>
              <div className="sm:col-span-2"></div>
            </div> */}
          </div>
          {/* ProviderContactDrawer for adding contacts */}
          <ProviderContactDrawer
            open={contactDrawerOpen}
            setOpen={setContactDrawerOpen}
            register={register}
            watch={watch}
            onAddContact={handleAddContact}
          />
          {selectedProvider && (
            <VendorListedServicesSuggestions
              provider={selectedProvider}
              providerId={selectedProvider.provider_id}
              mode="vqq"
              rfis={rfis}
              templateOptions={optionsForMultiSelect || []}
              selectedTemplates={selectedServiceCategories}
              onSelectedTemplatesChange={setServicesWithUserTracking}
              gridClassName="ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 lg:py-3"
              contentClassName="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]"
            />
          )}
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Questionnaire Templates
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3"></span>{" "}
              <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                <Controller
                  name="serviceCategories"
                  control={control}
                  render={({ field }) => (
                    <TemplateGroupedDropdown
                      placeholder="Select Questionnaire Templates"
                      options={optionsForMultiSelect || []}
                      rfiList={rfis || []}
                      lockedValue="106c4bdc-e1c2-4fca-9ab6-745b995c65ee"
                      selected={field.value || []}
                      suggestedValues={suggestedTemplateValues}
                      onChange={setServicesWithUserTracking}
                    />
                  )}
                />
              </div>
              {errors.serviceCategories && (
                <p className="text-red-500 text-sm">
                  {errors.serviceCategories.message}
                </p>
              )}
            </div>
          </div>
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Location
            </h1>

            <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
              {availableLocations.length > 0 &&
              availableLocations[0].id !== "new-location" ? (
                <>
                  <div className="sm:max-w-xl relative rfi-location-dropdown">
                    <div
                      className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.rfiLocation ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.rfiLocation ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setIsRFILocationDropdownOpen(
                            !isRFILocationDropdownOpen,
                          )
                        }
                        onBlur={(e) =>
                          onBlurSelectWithDelay(e, setIsRFILocationDropdownOpen)
                        } // Use the new onBlurSelect function
                        className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                      >
                        <span className="flex items-center truncate text-gray-900">
                          {currentSelectedRFILocation ? (
                            (() => {
                              const selected = availableLocations.find(
                                (loc) =>
                                  loc.id === currentSelectedRFILocation?.id,
                              );
                              return selected ? (
                                selected.id === "new-location" ? (
                                  // Special styling for "New Location" option
                                  <span className="italic text-gray-500">
                                    Request a location that is not listed above
                                  </span>
                                ) : (
                                  // Regular location display
                                    <>
                                      <span className="font-medium">
                                        {selected.label}:
                                      </span>{" "}
                                      <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={selected.fullAddress}>
                                        {selected.fullAddress}
                                      </CustomTooltip>
                                    </>
                                )
                              ) : (
                                "Select VQQ location"
                              );
                            })()
                          ) : (
                            <span className="text-gray-500">
                              Select VQQ location
                            </span>
                          )}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDownIconSolid
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    </div>

                    {isRFILocationDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {availableLocations.map((location) => (
                          <div
                            key={location.id}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                              location.isNewLocation
                                ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                : ""
                            } ${
                              currentSelectedRFILocation?.id === location.id
                                ? "text-indigo-600 bg-indigo-50"
                                : "text-gray-900 hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              currentSetSelectedRFILocation(location);
                              setIsRFILocationDropdownOpen(false);
                            }}
                          >
                            <span className="flex items-center truncate">
                              {location.id === "new-location" ? (
                                // Special styling for "New Location"
                                <span className="italic text-gray-500">
                                  Request a location that is not listed above
                                </span>
                              ) : (
                                // Regular location display
                                <>
                                  <span className="font-medium">
                                    {location.label}:
                                  </span>{" "}
                                  <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={location.fullAddress}>
                                    {location.fullAddress}
                                  </CustomTooltip>
                                </>
                              )}
                            </span>
                            {currentSelectedRFILocation?.id === location.id && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                <CheckIcon
                                  className="h-5 w-5 text-indigo-600"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // No locations available - show message like preferred contact
                <div className="sm:max-w-xl text-sm sm:pt-1.5">
                  <span className="text-gray-500 italic">
                    Please first select or add a vendor in the section above.
                  </span>
                </div>
              )}
              {errors.rfiLocation && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.rfiLocation}
                </p>
              )}
            </div>
          </div>
          {currentSelectedRFILocation?.id === "new-location" && (
            <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 lg:py-3">
              <div className="flex items-center justify-between sm:pt-1.5">
                <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                  Add new Location
                </h1>
                {availableLocations?.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      currentSetSelectedRFILocation(
                        availableLocations?.find(loc => loc.location_type === "primary") || availableLocations[0] || null,
                      );
                      setPrimaryAddress("");
                      setPrimaryCity("");
                      setPrimaryState("");
                      setPrimaryCountry("");
                    }}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Remove location"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              <LocationFields
                control={control}
                errors={errors}
                handleOnLoad={handleOnLoad}
                handlePlaceChanged={handlePlaceChanged}
                address={primaryAddress}
                setAddress={setPrimaryAddress}
                setValue={setValue}
                onChangeLocation={() => {}}
                city={primaryCity}
                setCity={setPrimaryCity}
                state={primaryState}
                setState={setPrimaryState}
                country={primaryCountry}
                setCountry={setPrimaryCountry}
                countrySearch={primaryCountrySearch}
                setCountrySearch={setPrimaryCountrySearch}
                isCountryDropdownOpen={isPrimaryCountryDropdownOpen}
                setIsCountryDropdownOpen={setIsPrimaryCountryDropdownOpen}
                isStateDropdownOpen={isPrimaryStateDropdownOpen}
                setIsStateDropdownOpen={setIsPrimaryStateDropdownOpen}
                stateSearch={primaryStateSearch}
                setStateSearch={setPrimaryStateSearch}
                classes="w-[330px] lg:w-[400px] xl:w-[500px]"
              />
            </div>
          )}

          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Expected Due Date
            </h1>{" "}
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                  <DatePickerController
                    key={watch("selectedProvider")?.provider_id ?? "none"}
                    control={control}
                    fieldName={"expectedDate"}
                    className="w-full"
                    minDate={new Date()}
                  />
                </div>
              </span>
              {errors.expectedDate && (
                <p className="text-red-500 text-sm">
                  {errors.expectedDate.message ||
                    "Expected due date is required"}
                </p>
              )}
            </div>
          </div>
          <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
            <h1 className="block text-sm font-medium text-gray-900">
              Additional Details
            </h1>
            <div>
              <textarea
                {...register("additionalDetails")}
                placeholder="Provide additional request details"
                className="border-gray-300 rounded-md text-sm  w-[330px] lg:w-[400px] xl:w-[500px] "
              />
            </div>
          </div>
          {riskAddOns.showAddOns && (
            <div className="ml-5 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 lg:py-3">
              <h1 className="block text-sm font-medium text-gray-900">
                Add-Ons
              </h1>
              <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                <fieldset className="rounded-md border border-gray-300 p-4">
                  <legend className="px-1 text-sm font-medium text-gray-900">
                    Risk Scoring &amp; Gap Analysis
                  </legend>
                  <div className="space-y-3">
                    {canRequestAutoScore && (
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableAutoScore}
                          onChange={(e) => setEnableAutoScore(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <span className="text-sm text-gray-900">
                          <span className="font-medium">
                            Diligent Risk Model Auto-Score
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500">
                            When the vendor submits, each response is auto-scored against the Diligent Risk Model and a Diligent admin reviews the findings before publishing. No gap-analysis comments required.
                          </span>
                        </span>
                      </label>
                    )}
                    {canRequestSmeScore && (
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableSmeScore}
                          onChange={(e) => setEnableSmeScore(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <span className="text-sm text-gray-900">
                          <span className="font-medium">
                            Diligent SME-Score and Gap Analysis
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500">
                            A Diligent subject-matter expert reviews every non-Compliant finding, adjusts severity as needed, and writes a gap-analysis comment for each one before publishing. Best for VQQs you intend to act on contractually.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                </fieldset>
              </div>
            </div>
          )}
          <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
            <button
              type="button"
              onClick={() => navigate("/RFI/History")}
              className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`inline-flex justify-center rounded-md ${
                isLoading || !canSubmit() || isDuringMaintenance() ? "bg-gray-300" : "bg-indigo-600"
              } px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                isLoading || !canSubmit() || isDuringMaintenance()
                  ? "hover:bg-gray-300"
                  : "hover:bg-indigo-500"
              } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
              disabled={isLoading || !canSubmit() || isDuringMaintenance()}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </FormProvider>

      <NotificationModal
        setOpen={setShowNotification}
        open={showNotification}
        title={isError ? "Error" : "VQQ Request Submitted"}
        desc={notificationMessage}
        error={isError}
      />
    </div>
  );
};
